import { BaseDomainError, type DomainErrorData, type TelErrorJson } from '@w/fe-utils'
export { i18tArgs } from '@w/fe-inv-i18n/src/i18n'

export type FeInvDomainErrorType =
  | 'AUTH_TOKEN_MISSING'
  | 'AUTH_CONNECTION_PROBLEM'
  | 'REQUEST_CANCELLED'
  | 'BAD_BROWSER_EXTENSION'
  | 'DATANORM_IMPORT_ERROR'

export function authTokenMissing(userMessage: unknown[], message?: string) {
  return new BaseDomainError('AUTH_TOKEN_MISSING' satisfies FeInvDomainErrorType, userMessage, {
    message,
    shouldContactSupport: true,
  })
}

/**
 * No token because a refresh failed without the auth service refusing it: the connection or the
 * service has a problem, the session is kept, and the request can succeed once a token is acquired
 * again. Nothing for support, unlike a token that is missing (INV-924).
 */
export function authConnectionProblem(userMessage: unknown[], message?: string) {
  return new BaseDomainError('AUTH_CONNECTION_PROBLEM' satisfies FeInvDomainErrorType, userMessage, {
    message,
    shouldContactSupport: false,
  })
}

/**
 * Whether `error` is an auth connection problem, also when a tRPC link wrapped it: the token getter
 * runs in the link's headers, before any request is sent, and tRPC keeps what it threw as `cause`.
 */
export function isAuthConnectionProblem(error: unknown): boolean {
  const cause = error instanceof Error ? error.cause : undefined
  return [error, cause].some(
    (candidate) =>
      candidate instanceof BaseDomainError &&
      candidate.domainErrorType === ('AUTH_CONNECTION_PROBLEM' satisfies FeInvDomainErrorType),
  )
}

export function requestCancelled(userMessage: unknown[], message?: string) {
  return new BaseDomainError('REQUEST_CANCELLED' satisfies FeInvDomainErrorType, userMessage, {
    message,
    shouldContactSupport: true,
  })
}

export function badBrowserExtension(userMessage: unknown[], message?: string) {
  return new BaseDomainError('BAD_BROWSER_EXTENSION' satisfies FeInvDomainErrorType, userMessage, {
    message,
    shouldContactSupport: false,
  })
}

export function datanormImportError(userMessage: unknown[], message?: string) {
  return new BaseDomainError('DATANORM_IMPORT_ERROR' satisfies FeInvDomainErrorType, userMessage, {
    message,
  })
}

/**
 * Rebuilds a domain error from its `toJSON()` form, e.g. after it crossed a worker boundary, where
 * structured cloning keeps only `message` and would lose the `userMessage` to localize.
 */
export function domainErrorFromJson(json: unknown): BaseDomainError | undefined {
  if (!json || typeof json !== 'object' || (json as TelErrorJson).type !== 'domain') {
    return undefined
  }
  const { errorData, message, stack } = json as TelErrorJson
  const data = errorData as Partial<DomainErrorData> | undefined
  if (!Array.isArray(data?.userMessage) || typeof data.domainErrorType !== 'string') {
    return undefined
  }
  const error = new BaseDomainError(data.domainErrorType, data.userMessage, { message })
  if (stack) error.stack = stack // where it was thrown, not where it was rebuilt
  return error
}
